import React from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useNavigate } from "react-router-dom";
import CaptureIntro from "./components/CaptureIntro";
import Capture from "./components/Capture";
import ProfileHome from "./components/ProfileHome";
import Review from "./components/Review";
import HowItWorks from "./components/HowItWorks";
import MatchesView from "./components/MatchesView";
import {
  submitTranscripts,
  getMatchmakerProfile,
  getTraits,
  deleteTrait,
  startMatchmakerProfile,
  getMyMatches,
  sendMatchInterest,
  passMatch,
  acknowledgeInterest,
  submitMatchFeedback,
} from "../../utilities/aiMatchmaker";
import { getSocket } from "../../utilities/socketService";

const INTRO_SEEN_KEY = "matchProfileIntroSeen";

const hasSeenIntro = () => {
  try {
    return window.localStorage.getItem(INTRO_SEEN_KEY) === "true";
  } catch (e) {
    return false;
  }
};

const markIntroSeen = () => {
  try {
    window.localStorage.setItem(INTRO_SEEN_KEY, "true");
  } catch (e) {}
};

const AIMatchmaker = () => {
  const navigate = useNavigate();
  const startedAtCapture = React.useRef(hasSeenIntro());
  const startMarkedRef = React.useRef(false);
  const [step, setStep] = React.useState("loading");
  const [profile, setProfile] = React.useState(null);
  const [traits, setTraits] = React.useState({ self: [], partner: [] });
  const [reviewTab, setReviewTab] = React.useState("me");
  const [cameFromHome, setCameFromHome] = React.useState(false);
  const [showHelp, setShowHelp] = React.useState(false);
  const [matches, setMatches] = React.useState([]);
  const [matchesLoading, setMatchesLoading] = React.useState(false);
  const [matchBusyId, setMatchBusyId] = React.useState(null);
  const [interestAck, setInterestAck] = React.useState(false);

  const [aboutMe, setAboutMe] = React.useState("");
  const [lookingFor, setLookingFor] = React.useState("");
  const [meStatus, setMeStatus] = React.useState("idle");
  const [lookStatus, setLookStatus] = React.useState("idle");
  const [meTranscriptId, setMeTranscriptId] = React.useState(null);
  const [lookTranscriptId, setLookTranscriptId] = React.useState(null);
  const [errorMsg, setErrorMsg] = React.useState(null);

  React.useEffect(() => {
    load();
  }, []);

  React.useEffect(() => {
    if (step === "capture" && !startMarkedRef.current) {
      startMarkedRef.current = true;
      startMatchmakerProfile();
    }
  }, [step]);

  React.useEffect(() => {
    const onReady = (data) => {
      const which =
        data && data.promptType === "partner_preference" ? "look" : "me";
      const setStatus = which === "me" ? setMeStatus : setLookStatus;
      if (data && data.status === "failed") {
        setStatus("idle");
        setErrorMsg("We couldn't process that note. Please try again.");
        return;
      }
      setStatus("done");
      if (which === "me") setMeTranscriptId(data.transcriptId);
      else setLookTranscriptId(data.transcriptId);
      refreshProfile();
    };

    let attached = null;
    const attach = (s) => {
      s.on("matchProfileReady", onReady);
      attached = s;
    };

    const existing = getSocket();
    let intervalId;
    if (existing) {
      attach(existing);
    } else {
      intervalId = setInterval(() => {
        const s = getSocket();
        if (s) {
          attach(s);
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (attached) attached.off("matchProfileReady", onReady);
    };
  }, []);

  const loadMatches = async ({ showSpinner } = {}) => {
    if (showSpinner) setMatchesLoading(true);
    let res;
    try {
      res = await getMyMatches();
    } catch (e) {
      res = null;
    }
    if (res && res.success) {
      setMatches(res.matches || []);
      setInterestAck(!!res.interestAck);
    }
    if (showSpinner) setMatchesLoading(false);
  };

  const handleAcknowledge = async () => {
    setInterestAck(true);
    try {
      await acknowledgeInterest();
    } catch (e) {}
  };

  const openMatches = () => {
    setStep("matches");
    loadMatches({ showSpinner: matches.length === 0 });
  };

  const handleInterested = async (matchId) => {
    setMatchBusyId(matchId);
    let res;
    try {
      res = await sendMatchInterest(matchId);
    } catch (e) {
      res = null;
    }
    if (res && res.success) {
      setMatches((prev) =>
        prev.map((m) =>
          m.id === matchId
            ? { ...m, youHearted: true, connected: !!res.connected }
            : m
        )
      );
    } else {
      setErrorMsg("We couldn't send your response. Please try again.");
    }
    setMatchBusyId(null);
    return res;
  };

  const handleDismissMatch = (matchId) => {
    setMatches((prev) => prev.filter((m) => m.id !== matchId));
  };

  const handleMatchFeedback = async (matchId, value) => {
    setMatches((prev) =>
      prev.map((m) => (m.id === matchId ? { ...m, feedback: value } : m))
    );
    try {
      await submitMatchFeedback(matchId, value);
    } catch (e) {
      setErrorMsg("We couldn't save your response. Please try again.");
    }
  };

  const handlePass = async (matchId) => {
    setMatchBusyId(matchId);
    let res;
    try {
      res = await passMatch(matchId);
    } catch (e) {
      res = null;
    }
    if (res && res.success) {
      setMatches((prev) => prev.filter((m) => m.id !== matchId));
    } else {
      setErrorMsg("We couldn't save that. Please try again.");
    }
    setMatchBusyId(null);
  };

  const refreshProfile = async () => {
    let p;
    try {
      p = await getMatchmakerProfile();
    } catch (e) {
      p = null;
    }
    if (!p || !p.success) return;
    setProfile(p);
    if (p.hasProfile) {
      let t;
      try {
        t = await getTraits();
      } catch (e) {
        t = null;
      }
      if (t && t.success) {
        setTraits({ self: t.self || [], partner: t.partner || [] });
      }
    }
  };

  const load = async () => {
    setStep("loading");
    let p;
    try {
      p = await getMatchmakerProfile();
    } catch (e) {
      p = null;
    }
    if (p && p.success && p.hasProfile) {
      let t;
      try {
        t = await getTraits();
      } catch (e) {
        t = null;
      }
      setProfile(p);
      setTraits(
        t && t.success
          ? { self: t.self || [], partner: t.partner || [] }
          : { self: [], partner: [] }
      );
      loadMatches();
      setStep("home");
    } else {
      setProfile(p && p.success ? p : { hasProfile: false });
      setStep(hasSeenIntro() ? "capture" : "intro");
    }
  };

  const startCapture = () => {
    markIntroSeen();
    setStep("capture");
  };

  const addNote = () => {
    setCameFromHome(true);
    setAboutMe("");
    setLookingFor("");
    setMeStatus("idle");
    setLookStatus("idle");
    setMeTranscriptId(null);
    setLookTranscriptId(null);
    setStep("capture");
  };

  const openReview = (which) => {
    setReviewTab(which === "partner" ? "look" : "me");
    setStep("review");
  };

  const submitPrompt = async (which) => {
    const setStatus = which === "me" ? setMeStatus : setLookStatus;
    const payload =
      which === "me"
        ? { aboutMe, lookingFor: "" }
        : { aboutMe: "", lookingFor };
    setStatus("submitting");
    let result;
    try {
      result = await submitTranscripts(payload);
    } catch (e) {
      result = null;
    }
    if (result && result.success) {
      setStatus("processing");
    } else {
      setStatus("idle");
      setErrorMsg(
        (result && result.error) ||
          "Something went wrong submitting your answers. Please try again."
      );
    }
  };

  const handleDelete = async (promptType, id) => {
    let res;
    try {
      res = await deleteTrait({ id, promptType });
    } catch (e) {
      res = null;
    }
    if (res && res.success) {
      setTraits((prev) => {
        const isSelf = promptType === "self_perception";
        return {
          self: isSelf ? prev.self.filter((t) => t.id !== id) : prev.self,
          partner: isSelf
            ? prev.partner
            : prev.partner.filter((t) => t.id !== id),
        };
      });
    } else {
      setErrorMsg((res && res.error) || "Could not delete that. Please try again.");
    }
  };

  const backToHome = cameFromHome || (profile && profile.hasProfile);
  const captureBack = () => {
    if (backToHome) return setStep("home");
    if (startedAtCapture.current) return navigate("/profile");
    return setStep("intro");
  };
  const captureDone = () => {
    if (backToHome) return setStep("home");
    return navigate("/profile");
  };

  const limitReached = !!(profile && profile.limitReached);

  const meChips = meTranscriptId
    ? traits.self.filter((t) => String(t.transcript) === String(meTranscriptId))
    : [];
  const lookChips = lookTranscriptId
    ? traits.partner.filter(
        (t) => String(t.transcript) === String(lookTranscriptId)
      )
    : [];

  let screen;
  if (step === "loading") {
    screen = (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  } else if (step === "intro") {
    screen = <CaptureIntro onStart={startCapture} />;
  } else if (step === "home") {
    screen = (
      <ProfileHome
        profile={profile}
        traits={traits}
        matches={matches}
        onAddNote={addNote}
        onReviewAll={openReview}
        onViewMatches={openMatches}
      />
    );
  } else if (step === "matches") {
    screen = (
      <MatchesView
        matches={matches}
        loading={matchesLoading}
        busyId={matchBusyId}
        interestAck={interestAck}
        onBack={() => setStep("home")}
        onPass={handlePass}
        onInterested={handleInterested}
        onAcknowledge={handleAcknowledge}
        onDismiss={handleDismissMatch}
        onFeedback={handleMatchFeedback}
      />
    );
  } else if (step === "review") {
    screen = (
      <Review
        traits={traits}
        onDelete={handleDelete}
        onBack={() => setStep("home")}
        initialTab={reviewTab}
      />
    );
  } else {
    screen = (
      <Capture
        aboutMe={aboutMe}
        lookingFor={lookingFor}
        setAboutMe={setAboutMe}
        setLookingFor={setLookingFor}
        meStatus={meStatus}
        lookStatus={lookStatus}
        meChips={meChips}
        lookChips={lookChips}
        limitReached={limitReached}
        onDeleteTrait={handleDelete}
        onSubmitPrompt={submitPrompt}
        onBack={captureBack}
        onDone={captureDone}
      />
    );
  }

  return (
    <>
      {step !== "loading" ? (
        <IconButton
          onClick={() => setShowHelp(true)}
          aria-label="How Matchmaker works"
          size="small"
          sx={{
            position: "fixed",
            top: 10,
            right: 10,
            zIndex: 1200,
            color: "text.secondary",
            "&:hover": { bgcolor: "transparent" },
          }}
        >
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      ) : null}
      {screen}
      <HowItWorks open={showHelp} onClose={() => setShowHelp(false)} />
      <Dialog
        open={!!errorMsg}
        onClose={() => setErrorMsg(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography sx={{ fontSize: 15, color: "text.primary" }}>
            {errorMsg}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorMsg(null)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AIMatchmaker;
